import ErrorDialog from "@/components/ErrorDialog"
import { authErrorMessage } from "@/lib/authErrors"
import { APP_LOGO, APP_NAME } from "@/lib/brand"
import { useAuth } from "@/hooks/useAuth"
import { isComingSoonEnabled } from "@/lib/launch"
import { lumi } from "@/lib/lumi"
import { privacyAdminOverview, privacySetComingSoon } from "@/lib/privacy"
import type { SiteSetting, WaitlistEmail } from "@/types"
import { useEffect, useState } from "react"
import { Link } from "react-router-dom"

type ReportRow = {
  _id?: string
  reporterId?: string
  targetType?: string
  targetId?: string
  reason?: string
  note?: string
  createdAt?: string
}

export default function AdminPage() {
  const { user, isAuthenticated, isAdmin, signOut } = useAuth()
  const [signingIn, setSigningIn] = useState(false)
  const [setting, setSetting] = useState<SiteSetting | null>(null)
  const [waitlist, setWaitlist] = useState<WaitlistEmail[]>([])
  const [reports, setReports] = useState<ReportRow[]>([])
  const [loading, setLoading] = useState(false)
  const [toggling, setToggling] = useState(false)
  const [error, setError] = useState("")
  const [status, setStatus] = useState("")

  const live = !isComingSoonEnabled(setting)

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) return
    let active = true
    const load = async () => {
      setLoading(true)
      try {
        const overview = await privacyAdminOverview()
        if (!active) return
        setSetting(overview.setting)
        setWaitlist(overview.waitlist)
        setReports((overview.reports || []) as ReportRow[])
      } catch (err) {
        console.error("Admin load failed:", err)
        if (active) setError(err instanceof Error ? err.message : "Could not load the admin panel.")
      } finally {
        if (active) setLoading(false)
      }
    }
    load()
    return () => {
      active = false
    }
  }, [isAuthenticated, isAdmin])

  const signInWithGoogle = async () => {
    setError("")
    setSigningIn(true)
    try {
      await lumi.auth.signInWithOAuth({ provider: "google" })
      window.location.href = "/admin"
    } catch (err) {
      setError(authErrorMessage(err))
      setSigningIn(false)
    }
  }

  const toggleLaunch = async () => {
    if (!isAdmin) return
    setToggling(true)
    setError("")
    setStatus("")
    try {
      const nextEnabled = live
      const updated = await privacySetComingSoon(nextEnabled)
      setSetting(updated)
      setStatus(nextEnabled ? "Coming soon page is on." : "KLIPO is live.")
    } catch (err) {
      console.error("Launch toggle failed:", err)
      setError(err instanceof Error ? err.message : "Could not update the launch toggle.")
    } finally {
      setToggling(false)
    }
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-[#1F1F1F] px-5 py-4">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={APP_LOGO} alt={APP_NAME} className="h-10 w-auto object-contain" />
            <div>
              <p className="font-[Syne] text-sm font-bold uppercase tracking-[0.18em] text-[#00FF66]">Control</p>
              <h1 className="text-lg font-semibold">KLIPO admin</h1>
            </div>
          </div>
          {isAuthenticated ? (
            <button
              type="button"
              onClick={() => {
                signOut()
                window.location.href = "/admin"
              }}
              className="rounded-full border border-[#1F1F1F] px-4 py-2 text-xs font-semibold text-[#A8A8A8]"
            >
              Sign out
            </button>
          ) : null}
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 py-8">
        {!isAuthenticated ? (
          <div className="mx-auto max-w-sm rounded-2xl border border-[#1F1F1F] bg-[#0B0B0B] p-5">
            <p className="text-sm text-[#A8A8A8]">Sign in with the @klipo Gmail account to open the KLIPO admin panel.</p>
            <button
              type="button"
              onClick={signInWithGoogle}
              disabled={signingIn}
              className="mt-5 w-full rounded-xl bg-[#00FF66] py-3 text-sm font-bold text-black disabled:opacity-60"
            >
              {signingIn ? "Opening Google..." : "Continue with Google"}
            </button>
          </div>
        ) : !isAdmin ? (
          <div className="rounded-2xl border border-[#1F1F1F] bg-[#0B0B0B] p-5">
            <h2 className="text-lg font-semibold">Access denied</h2>
            <p className="mt-2 text-sm text-[#A8A8A8]">
              {user?.email || "This account"} is signed in, but it is not a KLIPO admin. Sign out, then continue with the @klipo Gmail account.
            </p>
            <button
              type="button"
              onClick={() => {
                signOut()
                window.location.href = "/admin"
              }}
              className="mt-5 w-full rounded-xl border border-[#1F1F1F] py-3 text-sm font-semibold text-white"
            >
              Sign out and use Google
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <section className="rounded-2xl border border-[#1F1F1F] bg-[#0B0B0B] p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.16em] text-[#6E6E6E]">Launch</p>
                  <h2 className="mt-1 text-xl font-semibold">{live ? "Website is live" : "Coming soon page is on"}</h2>
                  <p className="mt-2 text-sm text-[#A8A8A8]">
                    Flip this to show or hide the public coming soon screen at klipo.media.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={toggleLaunch}
                  disabled={toggling || loading}
                  aria-pressed={live}
                  className={`h-7 w-12 shrink-0 rounded-full transition ${live ? "bg-[#00FF66]" : "bg-[#1F1F1F]"}`}
                >
                  <span className={`block h-6 w-6 translate-y-0.5 rounded-full bg-black transition ${live ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
              </div>
              {status ? <p className="mt-3 text-sm text-[#00FF66]">{status}</p> : null}
              <Link to="/" className="mt-4 inline-flex text-sm font-semibold text-[#00FF66]">
                Open public site
              </Link>
            </section>

            <section className="rounded-2xl border border-[#1F1F1F] bg-[#0B0B0B] p-5">
              <h2 className="text-sm font-semibold">Waitlist ({waitlist.length})</h2>
              <div className="mt-3 max-h-72 space-y-2 overflow-auto text-sm text-[#A8A8A8]">
                {loading ? (
                  <p>Loading...</p>
                ) : waitlist.length === 0 ? (
                  <p>No emails yet.</p>
                ) : (
                  waitlist.map((item) => (
                    <div key={item._id || item.email} className="flex items-center justify-between gap-3 border-b border-[#1F1F1F] py-2">
                      <span className="truncate">{item.email}</span>
                      <span className="shrink-0 text-xs text-[#6E6E6E]">{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : ""}</span>
                    </div>
                  ))
                )}
              </div>
            </section>

            {reports.length > 0 ? (
              <section className="rounded-2xl border border-[#1F1F1F] bg-[#0B0B0B] p-5">
                <h2 className="text-sm font-semibold">Reports ({reports.length})</h2>
                <div className="mt-3 space-y-2 text-sm text-[#A8A8A8]">
                  {reports.map((item) => (
                    <p key={item._id}>
                      {item.reason || "report"} · {item.targetType} {item.targetId}
                    </p>
                  ))}
                </div>
              </section>
            ) : null}
          </div>
        )}
      </main>

      {error ? <ErrorDialog title="Admin Error" message={error} onClose={() => setError("")} /> : null}
    </div>
  )
}
